import tenantRoutes from '@routes';
import { Typography } from 'antd';
import React, { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Card, Drawer, Flex, Icon } from '../../../../components/common';
import {
  canManageAgencyQuota,
  getQuotaInfoSections,
  getQuotaSideDrawerProps,
  isAgencyAgent,
} from '../../../../utility/quota';
import ExpandableBottomSheet from '../../../../components/assign-products/expandable-bottom-sheet';

const { Title, Text } = Typography;

// "What are Products?" — the quota counterpart of the Bayut Credits drawer.
const QuotaInfoDrawer = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { isMobile, locale, isMemberArea } = useSelector((state) => state?.app?.AppConfig) || {};
  const user = useSelector((state) => state?.app?.loginUser?.user);
  const isAgent = isAgencyAgent(user);

  // Profolio-Lite has no Product Usage page, so the words stay plain text there.
  const usageLink = isMemberArea ? (
    t('Product Usage')
  ) : (
    <Link style={{ textDecoration: 'underline' }} to={tenantRoutes.app()?.credits_usage?.path}>
      {t('Product Usage')}
    </Link>
  );

  const canManage = canManageAgencyQuota(user);
  const content = useMemo(() => getQuotaInfoSections({ isAgent, canManage }), [isAgent, canManage]);

  // Desktop: side drawer. Mobile: bottom sheet (full screen only when the user drags it up).
  const Container = isMobile ? ExpandableBottomSheet : Drawer;
  return (
    <Container
      ref={ref}
      title={<div className="fw-700">{t('What are Products?')}</div>}
      {...(!isMobile && { ...getQuotaSideDrawerProps(locale), hideButton: 'true' })}
      footer={null}
    >
      <Text type="secondary" className={isMobile && 'fz-12 color-gray-dark'}>
        {t('Your package gives you products. Each one does one job for a listing and has its own balance.')}
      </Text>
      <Title level={isMobile ? 5 : 4}>{t('Here’s some information to help you get started:')}</Title>
      <Flex vertical gap={isMobile ? '10px' : '16px'}>
        {content.map((item) => (
          <Card
            bodyStyle={{ padding: isMobile ? '12px' : '20px' }}
            style={{ backgroundColor: item.backgroundColor, border: 0, borderRadius: isMobile && '6px' }}
            key={item.id}
          >
            <Flex vertical align="start" gap={isMobile ? '10px' : '16px'}>
              <Icon icon={item.icon} size={isMobile ? 24 : 32} />
              <Title className="mb-0" level={isMobile ? 5 : 4}>
                {t(item.heading)}
              </Title>
              <Text type="secondary" className={isMobile && 'fz-12'}>
                {t(item.text)} {item.withUsageLink && usageLink}
              </Text>
            </Flex>
          </Card>
        ))}
      </Flex>
    </Container>
  );
});

QuotaInfoDrawer.displayName = 'QuotaInfoDrawer';

export default QuotaInfoDrawer;
